import React from 'react';
import { Eye, CheckCircle, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { SortableHeader } from '@/components/ui/sortable-header';
import { useTableData } from '@/hooks/useTableData';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { getTransformationStatusLabel, getTransformationStatusColor } from '@/types/transformation.types';
import type { TransformationOrder, TransformationRule } from '@/types/transformation.types';

interface TransformationOrdersTableProps {
  orders: TransformationOrder[];
  rules: TransformationRule[];
  getIngredientById: (id: number | string) => any;
  formatDate: (date: string) => string;
  getAvailableActions: (order: TransformationOrder) => any[];
}

type SortField = 'orderNumber' | 'sourceQuantity' | 'sourceIngredient' | 'targetIngredient' | 'efficiency' | 'status' | 'createdAt';

export const TransformationOrdersTable: React.FC<TransformationOrdersTableProps> = ({
  orders,
  rules,
  getIngredientById,
  formatDate,
  getAvailableActions
}) => {
  // Configuración del hook reutilizable
  const fieldLabels: Record<SortField, string> = {
    orderNumber: 'Orden',
    sourceQuantity: 'Cantidad',
    sourceIngredient: 'Insumo Origen',
    targetIngredient: 'Insumo Destino',
    efficiency: 'Eficiencia',
    status: 'Estado',
    createdAt: 'Fecha'
  };

  // Extractor personalizado para campos complejos
  const sortValueExtractor = (order: TransformationOrder, field: SortField): any => {
    switch (field) {
      case 'orderNumber':
        return order.orderNumber.toLowerCase();
      case 'sourceQuantity':
        return order.sourceQuantity || order.requestedSourceQuantity || order.plannedSourceQuantity || order.actualSourceQuantity || 0;
      case 'sourceIngredient':
        const rule = rules.find(r => r.id === order.transformationRuleId);
        const sourceIngredient = getIngredientById(rule?.sourceIngredientId || 0);
        return (sourceIngredient?.name || 'Insumo Origen').toLowerCase();
      case 'targetIngredient':
        const targetRule = rules.find(r => r.id === order.transformationRuleId);
        const targetIngredient = getIngredientById(targetRule?.targetIngredientId || 0);
        return (targetIngredient?.name || 'Insumo Destino').toLowerCase();
      case 'efficiency':
        return order.efficiency ?? 0;
      case 'status':
        // Orden de prioridad para estados
        const statusOrder = {
          'PENDING': 4,
          'IN_PROGRESS': 3,
          'COMPLETED': 2,
          'CANCELLED': 1
        };
        return statusOrder[order.status as keyof typeof statusOrder] || 0;
      case 'createdAt':
        return new Date(order.createdAt);
      default:
        return order.orderNumber;
    }
  };

  // Usar el hook reutilizable
  const tableData = useTableData<TransformationOrder, SortField>(
    {
      data: orders,
      defaultSortField: 'createdAt',
      defaultSortDirection: 'desc',
      itemsPerPage: 10,
      sortValueExtractor
    },
    fieldLabels
  );

  if (orders.length === 0) {
    return (
      <div className="bg-white rounded-lg border">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">Órdenes de Transformación</h3>
          <p className="text-sm text-gray-600 mt-1">Lista de todas las órdenes de transformación registradas</p>
        </div>
        <div className="text-center py-12">
          <div className="text-gray-500">No se encontraron órdenes de transformación</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border">
      <div className="px-6 py-4 border-b">
        <h3 className="text-lg font-semibold">Órdenes de Transformación</h3>
        <p className="text-sm text-gray-600 mt-1">Lista de todas las órdenes de transformación registradas</p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortableHeader
                field="orderNumber"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Orden
              </SortableHeader>
              <SortableHeader
                field="sourceIngredient"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Insumo Origen
              </SortableHeader>
              <SortableHeader
                field="targetIngredient"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Insumo Destino
              </SortableHeader>
              <SortableHeader
                field="efficiency"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Eficiencia
              </SortableHeader>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sucursal
              </th>
              <SortableHeader
                field="status"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center w-32"
              >
                Estado
              </SortableHeader>
              <SortableHeader
                field="createdAt"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left w-24"
              >
                Fecha
              </SortableHeader>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableData.pagination.paginatedData.map((order) => {
              const rule = rules.find(r => r.id === order.transformationRuleId);

              // Obtener información de sources y targets (nueva estructura)
              const firstSource = order.sources?.[0];
              const firstTarget = order.targets?.[0];
              const additionalSourcesCount = (order.sources?.length || 0) - 1;
              const additionalTargetsCount = (order.targets?.length || 0) - 1;

              // Fallback a la estructura antigua si no existe la nueva
              const sourceIngredientId = firstSource?.ingredientId || rule?.sourceItems?.[0]?.ingredientId || rule?.sourceIngredientId;
              const targetIngredientId = firstTarget?.ingredientId || rule?.targetItems?.[0]?.ingredientId || rule?.targetIngredientId;

              const sourceIngredient = firstSource?.ingredient || getIngredientById(sourceIngredientId || 0);
              const targetIngredient = firstTarget?.ingredient || getIngredientById(targetIngredientId || 0);

              // Obtener cantidades de la nueva estructura con fallback a la antigua
              const sourceQuantity = firstSource?.requestedQuantity || order.sourceQuantity || order.requestedSourceQuantity || order.plannedSourceQuantity || 0;
              const sourceActualQuantity = firstSource?.actualQuantity || order.actualSourceQuantity || 0;
              const sourceUnit = firstSource?.unit || sourceIngredient?.unit || 'kg';

              const targetExpectedQuantity = firstTarget?.expectedQuantity || order.targetQuantity || order.expectedTargetQuantity || 0;
              const targetActualQuantity = firstTarget?.actualQuantity || order.actualTargetQuantity || 0;
              const targetUnit = firstTarget?.unit || targetIngredient?.unit || 'kg';

              // Calcular total de cantidad cuando hay múltiples sources
              const totalSourceQuantity = order.sources?.reduce((sum, s) => sum + (s.requestedQuantity || 0), 0) || sourceQuantity;

              return (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {order.orderNumber.replace('TRANS-', '').replace('TRF-', '')}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-1">
                      {order.sources && order.sources.length > 0 ? (
                        order.sources.map((source, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <span className="font-medium text-gray-900">{source.ingredient?.name || 'N/A'}</span>
                            <span className="text-blue-600 font-medium">{source.requestedQuantity}</span>
                            <span className="text-gray-400 text-xs">{source.unit}</span>
                          </div>
                        ))
                      ) : (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium text-gray-900">{sourceIngredient?.name || 'Insumo Origen'}</span>
                          <span className="text-blue-600 font-medium">{sourceQuantity}</span>
                          <span className="text-gray-400 text-xs">{sourceUnit}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-1">
                      {order.targets && order.targets.length > 0 ? (
                        order.targets.map((target, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <span className="font-medium text-gray-900">{target.ingredient?.name || 'N/A'}</span>
                            <span className="text-green-600 font-medium">{target.actualQuantity || target.expectedQuantity}</span>
                            <span className="text-gray-400 text-xs">{target.unit}</span>
                          </div>
                        ))
                      ) : (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium text-gray-900">{targetIngredient?.name || 'Insumo Destino'}</span>
                          <span className="text-green-600 font-medium">{targetActualQuantity || targetExpectedQuantity}</span>
                          <span className="text-gray-400 text-xs">{targetUnit}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className={`text-sm font-medium ${(order.efficiency ?? 0) > 90 ? 'text-green-600' : (order.efficiency ?? 0) > 70 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {order.efficiency?.toFixed(1) ?? '0.0'}%
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-gray-900">{(order as any).location?.name || 'N/A'}</span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <Badge className={getTransformationStatusColor(order.status)}>
                      {getTransformationStatusLabel(order.status)}
                    </Badge>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900">{formatDate(order.createdAt)}</div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <div className="flex flex-col gap-1 items-center">
                      <div className="flex gap-1 justify-center">
                        {/* View Details Dialog - Always available */}
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant="outline" size="sm" className="text-xs px-2 py-1 h-7 text-blue-600 border-blue-300 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-400" title="Ver detalles">
                              <Eye className="h-3 w-3 mr-1" />
                              Ver
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-3xl">
                            <DialogHeader>
                              <DialogTitle>Detalles de la Orden {order.orderNumber}</DialogTitle>
                              <DialogDescription>
                                Información completa de la orden de transformación
                              </DialogDescription>
                            </DialogHeader>
                            {/* Información General */}
                            <div className="grid gap-4 md:grid-cols-2">
                              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                                <h4 className="font-semibold mb-3 text-gray-900">Información General</h4>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Estado:</span>
                                    <Badge className={getTransformationStatusColor(order.status)}>{getTransformationStatusLabel(order.status)}</Badge>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Responsable:</span>
                                    <span className="font-medium text-gray-900">{order.requestedByEmployee?.name || 'N/A'}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                                <h4 className="font-semibold mb-3 text-gray-900">Transformación</h4>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Eficiencia:</span>
                                    <span className="font-medium text-gray-900">{order.efficiency?.toFixed(1) ?? '0.0'}%</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Procesado por:</span>
                                    <span className="font-medium text-gray-900">{order.processedByEmployee?.name || 'N/A'}</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Información de ingredientes */}
                            <div className="grid gap-4 md:grid-cols-2">
                              <div>
                                <h4 className="font-semibold mb-3 text-gray-900 border-b border-gray-200 pb-2">
                                  {(order.sources && order.sources.length > 1) ? 'Ingredientes Origen' : 'Ingrediente Origen'}
                                </h4>
                                <div className="space-y-3">
                                  {order.sources && order.sources.length > 0 ? (
                                    order.sources.map((source, index) => (
                                      <div key={index} className="rounded-lg border border-orange-200 bg-orange-50 p-3">
                                        <div className="space-y-2 text-sm">
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Nombre:</span>
                                            <span className="font-semibold text-orange-900">{source.ingredient?.name || 'N/A'}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Unidad:</span>
                                            <span className="font-medium text-gray-900">{source.unit}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Cantidad:</span>
                                            <span className="font-medium text-gray-900">{source.requestedQuantity}</span>
                                          </div>
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
                                      <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Nombre:</span>
                                          <span className="font-semibold text-orange-900">{order.transformationRule?.sourceIngredient?.name || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Unidad:</span>
                                          <span className="font-medium text-gray-900">{order.transformationRule?.sourceIngredient?.unitName || order.sourceUnit || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Cantidad:</span>
                                          <span className="font-medium text-gray-900">{order.sourceQuantity || order.plannedSourceQuantity || 0}</span>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div>
                                <h4 className="font-semibold mb-3 text-gray-900 border-b border-gray-200 pb-2">
                                  {(order.targets && order.targets.length > 1) ? 'Ingredientes Destino' : 'Ingrediente Destino'}
                                </h4>
                                <div className="space-y-3">
                                  {order.targets && order.targets.length > 0 ? (
                                    order.targets.map((target, index) => (
                                      <div key={index} className="rounded-lg border border-green-200 bg-green-50 p-3">
                                        <div className="space-y-2 text-sm">
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Nombre:</span>
                                            <span className="font-semibold text-green-900">{target.ingredient?.name || 'N/A'}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Unidad:</span>
                                            <span className="font-medium text-gray-900">{target.unit}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Esperada:</span>
                                            <span className="font-medium text-gray-900">{target.expectedQuantity}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Obtenida:</span>
                                            <span className="font-medium text-gray-900">{target.actualQuantity || 'Pendiente'}</span>
                                          </div>
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                                      <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Nombre:</span>
                                          <span className="font-semibold text-green-900">{order.transformationRule?.targetIngredient?.name || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Unidad:</span>
                                          <span className="font-medium text-gray-900">{order.transformationRule?.targetIngredient?.unitName || order.targetUnit || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Esperada:</span>
                                          <span className="font-medium text-gray-900">{order.targetQuantity || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Obtenida:</span>
                                          <span className="font-medium text-gray-900">{order.actualTargetQuantity || 'Pendiente'}</span>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {order.notes && (
                              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                                <h4 className="font-semibold mb-2 text-blue-900">Notas</h4>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{order.notes}</p>
                              </div>
                            )}
                          </DialogContent>
                        </Dialog>

                        {/* First action button (if exists) */}
                        {getAvailableActions(order)
                          .filter(action => action.type !== 'view')
                          .slice(0, 1)
                          .map((action, index) => {
                            const IconComponent = action.icon;
                            const getButtonClasses = (actionType: string) => {
                              switch (actionType) {
                                case 'complete':
                                  return "text-xs px-2 py-1 h-7 text-green-600 border-green-300 hover:bg-green-50 hover:text-green-700 hover:border-green-400";
                                case 'cancel':
                                  return "text-xs px-2 py-1 h-7 text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700 hover:border-red-400";
                                default:
                                  return "text-xs px-2 py-1 h-7";
                              }
                            };

                            const getButtonText = (actionType: string) => {
                              switch (actionType) {
                                case 'complete': return 'Completar';
                                case 'cancel': return 'Cancelar';
                                default: return 'Acción';
                              }
                            };

                            return (
                              <Button
                                key={index}
                                variant="outline"
                                size="sm"
                                className={getButtonClasses(action.type)}
                                onClick={action.onClick}
                                title={action.label}
                              >
                                <IconComponent className="h-3 w-3 mr-1" />
                                {getButtonText(action.type)}
                              </Button>
                            );
                          })}
                      </div>

                      {/* Second row for additional buttons */}
                      {getAvailableActions(order)
                        .filter(action => action.type !== 'view')
                        .slice(1).length > 0 && (
                        <div className="flex gap-1 justify-center">
                          {getAvailableActions(order)
                            .filter(action => action.type !== 'view')
                            .slice(1, 3)
                            .map((action, index) => {
                              const IconComponent = action.icon;
                              const getButtonClasses = (actionType: string) => {
                                switch (actionType) {
                                  case 'complete':
                                    return "text-xs px-2 py-1 h-7 text-green-600 border-green-300 hover:bg-green-50 hover:text-green-700 hover:border-green-400";
                                  case 'cancel':
                                    return "text-xs px-2 py-1 h-7 text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700 hover:border-red-400";
                                  default:
                                    return "text-xs px-2 py-1 h-7";
                                }
                              };

                              const getButtonText = (actionType: string) => {
                                switch (actionType) {
                                  case 'complete': return 'Completar';
                                  case 'cancel': return 'Cancelar';
                                  default: return 'Acción';
                                }
                              };

                              return (
                                <Button
                                  key={index + 1}
                                  variant="outline"
                                  size="sm"
                                  className={getButtonClasses(action.type)}
                                  onClick={action.onClick}
                                  title={action.label}
                                >
                                  <IconComponent className="h-3 w-3 mr-1" />
                                  {getButtonText(action.type)}
                                </Button>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Sort info */}
      <div className="mt-2 px-6 text-sm text-gray-500">
        Ordenado por: <span className="font-medium">
          {tableData.sortInfo.label}
        </span> ({tableData.sortInfo.direction === 'asc' ? 'Ascendente' : 'Descendente'})
      </div>

      {/* Pagination */}
      <div className="px-6 pb-4">
        <Pagination
          currentPage={tableData.pagination.currentPage}
          totalPages={tableData.pagination.totalPages}
          onPageChange={tableData.pagination.goToPage}
          showingFrom={tableData.pagination.showingFrom}
          showingTo={tableData.pagination.showingTo}
          totalItems={tableData.pagination.totalItems}
          itemsPerPage={tableData.pagination.itemsPerPage}
          onItemsPerPageChange={tableData.pagination.setItemsPerPage}
        />
      </div>
    </div>
  );
};